//-----------------------------------------------------------------------
// <copyright file="auditComponent.ts" company="LGT India">
//     Author: Sivapriya
//     Copyright (c) LGT India. All rights reserved.
// </copyright>
//-----------------------------------------------------------------------
import { HttpClient } from "@angular/common/http";
import { Component, Injectable, ViewChild } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { NativeDateAdapter, provideNativeDateAdapter } from "@angular/material/core";
import { MatDatepickerInputEvent } from "@angular/material/datepicker";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatSort, Sort } from "@angular/material/sort";
import { saveAs } from 'file-saver';
import { DialogBoxComponent } from "../dialog-box/dialog-box.component";
import { ApiService } from "../services/api.service";
import { AppComponent } from "../app.component";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";

@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  override format(date: Date, displayFormat: Object): string { // Add the 'override' modifier here
    if (displayFormat === 'input') {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return date.toDateString();
  }
}


export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'LL',
  },
  display: {
    dateInput: 'input', // Use the 'input' format here
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};
@Component({
  selector: "app-audit",
  styleUrls: ["audit.component.css"],
  templateUrl: "audit.component.html",
  providers: [provideNativeDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }, 
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
})
export class AuditComponent {
  showDownloadSection: boolean = true;
  isError: boolean = false;
  isErrorSnack: boolean = false;
  isTrue: boolean = false;
  isLoading: boolean = false;
  loading: boolean = false;
  showSpinner: boolean = true;
  refreshToken: string = "";
  dateFrom = new FormControl("", [Validators.required, Validators.required]);
  dateTo = new FormControl("", [Validators.required, Validators.required]);
  i: number = 0;
  pageEvent = { page: 0, pageSize: 1, totalPage: 0, totalRecords: 0 };
  notFound: string = "not Found";
  showDatepicker: boolean = false;
  data: any;
  public apiData: any[] = [];
  public downloadData: string[] = [];
  displayedColumns: string[] = [
    "no",
    "eventId",
    "date",
    "user",
    "activity",
    "event",
    "eventstatus",
  ];
  Download: string;
  Calander: string;
  fromDate: string = new Date().toISOString().split("T")[0];
  toDate: string = new Date().toISOString().split("T")[0];
  page: number = 1;
  size: number = 10;
  sortField: string = "";
  sortType: string = "";
  noData: string;
  errorData: string = "";
  chooseDate: string;
  now = new Date();
  year = this.now.getFullYear(); // Get the full year (e.g., 2024)
  month = (this.now.getMonth() + 1).toString().padStart(2, "0"); // Get the month (zero-based index, so add 1)
  date = this.now.getDate().toString().padStart(2, "0"); // Get the day of the month
  hours = this.now.getHours().toString().padStart(2, "0"); // Get the hours
  minutes = this.now.getMinutes().toString().padStart(2, "0"); // Get the minutes
  roomsFilter: { date: any } = { date: null };
  constructor(
    private app: AppComponent,
    public dialog: MatDialog,
    private http: HttpClient,
    private apiService: ApiService,
    private snackBar: MatSnackBar,

  ) {
    this.Download = "/assets/icons/download.svg";
    this.Calander = "/assets/icons/calander.svg";
    this.noData = "/assets/icons/noData.jpg";
    this.chooseDate = "assets/icons/chooseDate.png";
  }
  dateOptions: any = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };
  timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false, // Set hour12 option to false
  };
  formattedDate = `auditlog_${this.year}${this.month}${this.date}_${this.hours}${this.minutes}`;
  formattedTime = this.now.toLocaleString("en-US", this.timeOptions);
  openDialog() {
    this.dialog.open(DialogBoxComponent);
  }

  editSymbol(element: any) {
    this.openDialog();
  }

  ngOnInit(): void {
    const currentDate = new Date();
    this.fromDate = this.formatDate(currentDate);
    this.toDate = this.formatDate(currentDate);
    setTimeout(() => {
      this.showSpinner = false;
    }, 1000);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = this.padZero(date.getMonth() + 1); // Months are zero based
    const day = this.padZero(date.getDate());
    return `${year}-${month}-${day}`;
  }

  private padZero(num: number): string {
    return num < 10 ? `0${num}` : `${num}`;
  }

  @ViewChild(MatSort) sort!: MatSort;

  onSortChange(sort: Sort): void {
    if (this.sortField === sort.active) {
      // If the current sort field is the same as the active field
      if (this.sortType === "asc") {
        // If the current sort type is ascending, switch to descending
        this.sortType = "desc";
      } else if (this.sortType === "desc") {
        // If the current sort type is descending, cancel sorting
        this.sortType = "";
      } else {
        // If the current sort type is empty, switch to ascending
        this.sortType = "asc";
      }
    } else {
      // If a new sort field is selected, default to ascending
      this.sortField = sort.active || "";
      this.sortType = "asc";
    }
    this.fetchAuditData();
  }

  isCurrentPage(page: number | string): boolean {
    return page === this.pageEvent.page;
  }

  getCustomPageRange(): (number | string)[] {
    const currentPage = this.pageEvent.page;
    const totalPage = this.pageEvent.totalPage;

    const pageRange = 3; // Set the desired pagination length

    const startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
    const endPage = Math.min(totalPage, startPage + pageRange - 1);

    const pages: (number | string)[] = [];

    if (currentPage > 1) {
      pages.push("<");
    }

    if (startPage > 1) {
      pages.push(1);
    }

    if (startPage > 2) {
      pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPage) {
      pages.push("...");
    }

    if (endPage !== totalPage) {
      pages.push(totalPage);
    }

    if (currentPage < totalPage) {
      pages.push(">");
    }

    return pages;
  }

  onPageClick(page: number | string): void {
    const totalPage = this.pageEvent.totalPage;

    if (page === "<") {
      if (this.pageEvent.page > 1) {
        this.pageEvent.page = this.pageEvent.page - 1;
      }
    } else if (page === ">") {
      if (this.pageEvent.page < totalPage) {
        this.pageEvent.page = this.pageEvent.page + 1;
      }
    } else if (typeof page === "number") {
      if (page >= 1 && page <= totalPage) {
        this.pageEvent.page = page;
      } else {
        console.warn("Invalid page number");
      }
    }
    this.fetchAuditData();
  }

  onShowing(size: number): void {}

  totalRecords: number = 100;

  fetchAuditData(): void {
    if (!this.isLoading) {
      const page = this.pageEvent.page;
      this.isLoading = true;
      const accessToken:any = sessionStorage.getItem("accessToken");
      this.apiService
        .getAudit(
          this.fromDate,
          this.toDate,
          page,
          this.size,
          this.sortField,
          this.sortType,
          accessToken,
          this.isError
        )
        .subscribe({
          next: (response) => {
            const responseData = response as any;
            if (responseData.status == 200) {
              this.apiData = responseData?.data?.data;
              const paginteData = responseData?.data;
              this.pageEvent = {
                page: paginteData.page,
                pageSize: paginteData.pageSize,
                totalPage: paginteData.totalPages,
                totalRecords: paginteData.totalRecords,
              };
            }
            this.isLoading = false;
          },
          error: (error) => {
            console.error("Error fetching transaction data:", error);
            this.isLoading = false;
            if (error.status === 403) {
              this.isError = true;
              this.isErrorSnack=true;
              this.openSnackBar("User logged out");
  
              setTimeout(() => {
                this.app.logout();
              }, 2000);
            }
            if (error.status === 401) {
              this.isError = true;
              this.isErrorSnack=true;

              const refreshToken: any = sessionStorage.getItem("refreshToken");
              // Call refreshToken method
              this.apiService.refreshToken(refreshToken).subscribe({
                next: (res:any) => {
                  this.refreshToken = res.data.accessToken
                  sessionStorage.removeItem("accessToken");
                  sessionStorage.removeItem("refreshToken");
                  if (res.data.accessToken && res.data.refreshToken) {
                    sessionStorage.setItem("accessToken", res.data.accessToken);
                    sessionStorage.setItem("refreshToken", res.data.refreshToken);
                  }
                  this.fetchAuditData();
                },
                error: (refreshError) => {
                  console.error("Error refreshing token:", refreshError);
                  this.isError = true;
                  this.isErrorSnack=true;
                  this.openSnackBar("User logged out");
    
                  setTimeout(() => {
                    this.app.logout();
                  }, 2000);
                },
              });
            }
          },
        });
    }
  }

  downloadAuditData(): void {
    this.formattedTime = '';
    // Generate the new current time
    const now = new Date();
    const hour = now.getHours().toString().padStart(2, '0');
    const minute = now.getMinutes().toString().padStart(2, '0');
    this.formattedTime = `auditlog_${this.year}${this.month}${this.date}_${hour}${minute}`;
    this.loading = true; // Set loading state to true when download starts
    const accessToken:any = sessionStorage.getItem("accessToken");
    this.apiService
      .getdownloadAudit(
        this.fromDate,
        this.toDate,
        this.sortField,
        this.sortType,
        accessToken,
        this.isError
      )
      .subscribe({
        next: (response: Blob) => {
          this.isErrorSnack = false;
          saveAs(response, `${this.formattedTime}.xlsx`, { autoBom: false });
          this.openSnackBar("Download Completed");
          this.loading = false; // Set loading state to false when download completes
        },
        error: (error) => {
          console.error("Error fetching downloadAuditData data:", error);
          if (error.status === 403) {
            this.isError = true;
            this.isErrorSnack=true;
            this.openSnackBar("User logged out");

            setTimeout(() => {
              this.app.logout();
            }, 2000);
          }
          if (error.status === 401) {
            this.isError = true;
            const refreshToken:any = sessionStorage.getItem("refreshToken");
            // Call refreshToken method
            this.apiService.refreshToken(refreshToken).subscribe({
              next: (res:any) => {
                this.refreshToken = res.data.accessToken
                sessionStorage.removeItem("accessToken");
                sessionStorage.removeItem("refreshToken");
                if (res.data.accessToken && res.data.refreshToken) {
                sessionStorage.setItem("accessToken", res.data.accessToken);
                sessionStorage.setItem("refreshToken", res.data.refreshToken);
                }
                this.downloadAuditData();
              },
              error: (refreshError) => {
                console.error("Error refreshing token:", refreshError);
                this.isError = true;
                 this.isErrorSnack = true;

                this.openSnackBar("User logged out");
  
                setTimeout(() => {
                  this.app.logout();
                }, 2000);
              },
            });
          }
          this.storeErrorData(error.error.text);
          this.loading = false; // Set loading state to false if there's an error
        },
      });
  }

  storeErrorData(errorText: string): void {
    this.errorData = errorText;
  }

  onDate(event: MatDatepickerInputEvent<Date>, pickerId: string): void {
    const targetId = pickerId;

    if (targetId === "picker") {
      const selectedDate = event.value;
      if (selectedDate) {
        const nextDate = new Date(selectedDate);
        nextDate.setDate(nextDate.getDate() + 1);
        this.fromDate = nextDate.toISOString().split("T")[0];
      } else {
        this.fromDate = "";
      }
    } else if (targetId === "calander") {
      const selectedDate = event.value;
      if (selectedDate) {
        const nextDate = new Date(selectedDate);
        nextDate.setDate(nextDate.getDate() + 1);
        this.toDate = nextDate.toISOString().split("T")[0];
      } else {
        this.toDate = "";
      }
    }
  }

  dateFilter: (date: Date | null) => boolean = (date: Date | null): boolean => {
    const today = new Date(this.fromDate);
    today.setHours(0, 0, 0, 0);
    return !date || date >= today;
  };

  onViewButtonClick() {
    this.isTrue = true;
    if (this.fromDate == "") {
      this.isError = true;
      this.isErrorSnack=true;

      this.openSnackBar("Please Select From Date");
    } else if (this.toDate == "") {
      this.isError = true;
      this.isErrorSnack=true;

      this.openSnackBar("Please Select To Date");
    } else if (this.toDate < this.fromDate) {
      this.isError = true;
      this.isErrorSnack=true;

      this.openSnackBar("To Date Should Be Greater Than From Date");
    } else {
      if (this.fromDate && this.toDate && this.isTrue) {
        this.fetchAuditData();
      }
    }
    this.showDownloadSection = false;
  }

  calculateMin(): number {
    let minvalue = (this.pageEvent.page - 1) * this.pageEvent.pageSize + 1;

    return minvalue > this.pageEvent.totalRecords
      ? this.pageEvent.totalRecords
      : minvalue;
  }

  calculateMax(): number {
    let minvalue = this.pageEvent.page * this.pageEvent.pageSize;
    return minvalue > this.pageEvent.totalRecords
      ? this.pageEvent.totalRecords
      : minvalue;
  }
  calculateTotal(): number {
    return this.pageEvent.totalRecords;
  }

  checkDataImage() {
    return "assets/icons/noData.jpg";
  }

  openSnackBar(message: string) {
    this.snackBar.open(message, "x", {
      duration: this.isErrorSnack ? 3000 : 5000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: this.isErrorSnack
        ? ["mat-mdc-snack-bar-container", "red"]
        : ["mat-mdc-snack-bar-container", "green"],
    });
  }
}
